import { useEffect, useState } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { APP_EVENTS, captureAppEvent } from 'src/services/posthogEvents';
import Sentry from 'src/services/sentry';

import { isSupportedCameraLocaleCode } from 'src/models/Camera/Settings/Language/CameraLocale.model';
import { Voice } from 'src/models/Camera/Settings/Language/GetLanguages.model';
import { SetLanguageType } from 'src/models/Camera/Settings/Language/SetLanguage.model';
import { I18NextNS } from 'src/models/Language.model';

import { useGetCameraLanguages } from 'src/hooks/camera/useGetCameraLanguages';
import { useGetCameraSettings } from 'src/hooks/camera/useGetCameraSettings';
import { useSetCameraLanguage } from 'src/hooks/camera/useSetCameraLanguage';

import { captureError } from 'src/utils/errorReporting';
const CameraLanguageDropdown = () => {
  const { t: tPages } = useTranslation(I18NextNS.PAGES);

  const { languages, getCameraLanguages } = useGetCameraLanguages();

  const {
    cameraSettings,
    getCameraSettings,
    setCameraSettings,
    isGettingCameraSettings,
  } = useGetCameraSettings();
  const { setCameraLanguage } = useSetCameraLanguage();

  useEffect(() => {
    getCameraLanguages();
    getCameraSettings();
  }, [getCameraLanguages, getCameraSettings]);

  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);

  const handleSetCameraLanguage = async (lang: Voice) => {
    try {
      if (!cameraSettings) {
        Sentry.captureMessage('Camera settings not found');
        return;
      }

      setIsUpdatingLanguage(true);
      const previousLanguage = cameraSettings.obj.settings.voice;
      await setCameraLanguage({
        lang: lang.value,
        type: SetLanguageType.Voice,
      });

      setCameraSettings({
        ...cameraSettings,
        obj: {
          ...cameraSettings?.obj,
          settings: {
            ...cameraSettings?.obj.settings,
            voice: lang.value,
          },
        },
      });
      captureAppEvent(APP_EVENTS.CAMERA_LANGUAGE_CHANGED, {
        from_language: previousLanguage,
        to_language: lang.value,
      });
    } catch (error) {
      captureError(error, 'Failed to set camera language');
      // TODO: Uncomment this after we receive the error message from the translation company
      // Api.alertBox({
      //   title: t('bootbox.title.Error', { ns: I18NextNS.COMMON }),
      //   message: t('message.error.failedToSetCameraLanguage', {
      //     ns: I18NextNS.COMMON,
      //   }),
      //   locale: language,
      //   type: 'error',
      // });
    } finally {
      setIsUpdatingLanguage(false);
    }
  };

  const supportedVoices = languages?.obj.voice.filter((lang: Voice) =>
    isSupportedCameraLocaleCode(lang.value),
  );

  return (
    <Dropdown>
      <Dropdown.Toggle
        variant="primary-100"
        data-test="camera-language-dropdown"
      >
        {isUpdatingLanguage || isGettingCameraSettings ? (
          <Spinner size="sm" className="me-2" />
        ) : (
          <>
            <strong>{tPages('newExam.cameraLanguage')}</strong>{' '}
            {(() => {
              const currentVoice = cameraSettings?.obj.settings.voice;

              const foundLangLocaleCode = supportedVoices?.find(
                (lang: Voice) => lang.value === currentVoice,
              )?.value;

              return (
                foundLangLocaleCode &&
                tPages(
                  `newExam.cameraLanguageDropdownItems.${foundLangLocaleCode}`,
                )
              );
            })()}
          </>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu>
        {supportedVoices?.map((lang: Voice) => (
          <Dropdown.Item
            key={lang.label}
            active={lang.value === cameraSettings?.obj.settings.voice}
            onClick={() => handleSetCameraLanguage(lang)}
          >
            {tPages(`newExam.cameraLanguageDropdownItems.${lang.value}`)}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default CameraLanguageDropdown;
